import { useState } from 'react';
import { monogram } from "./Ui";
import { ArrowLeft, Building2, Search } from "lucide-react";
import ClientSwitcher from "./ClientSwitcher";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { CLIENT_GROUPS, CLIENT_PATHS, canOpenClientPage, clientLink, entryPages } from "../nav";
import { clientIdPage, pageMatches, routeMode } from "../sidebar-mode";

const linkClass = ({ isActive }: { isActive: boolean }) =>
  isActive ? "nav-link active" : "nav-link";


/**
 * The Clients half of the sidebar.
 *
 * LIST face: every client, one row each. Shown on /clients, and whenever you
 * peek at Clients from an agency page.
 * CLIENT face: shown while you are on one of a client's pages. The client's
 * name heads the panel (and switches client in place), then its pages.
 */
export default function ClientNav({
  hideLabels,
  onNavigate,
}: {
  hideLabels: boolean;
  onNavigate: () => void;
}) {
  const [search, setSearch] = useState('');
  const { clients, clientId, client, setClientId } = useClientSelection();
  const { user } = useAuth();
  const { pathname, search: query } = useLocation();
  const navigate = useNavigate();
  // A profile or ads report: the path names the client.
  const idPage = clientIdPage(pathname);
  const onClientPage =
    idPage !== null ||
    (routeMode(pathname, CLIENT_PATHS, []) === "clients" && !pathname.startsWith("/clients"));

  if (clients.loading && !clients.data)
    return <p className="client-nav-note">{hideLabels ? "…" : "Loading clients…"}</p>;
  if (clients.error)
    return (
      <div className="client-nav-note">
        {!hideLabels && <span>Clients could not load.</span>}
        <button type="button" className="text-button" onClick={clients.reload}>
          Retry
        </button>
      </div>
    );

  const list = clients.data ?? [];

  /* ────────────────────────────── CLIENT face ────────────────────────────── */
  if (onClientPage && client) {
    const switchTo = (id: number) => {
      if (!idPage) return setClientId(id);
      // Keep the page's own settings (the ads date range); drop anything tied to the old client.
      const keep = new URLSearchParams(query);
      for (const key of ["client_id", "record", "report", "session", "task"]) keep.delete(key);
      navigate(`${idPage.base}/${id}${keep.toString() ? `?${keep}` : ""}`);
    };
    return (
      <nav className="primary-nav" aria-label={`${client.name} navigation`}>
        {/* Pinned (shell.css): the client's pages scroll under it, so which client is open never scrolls away. */}
        <div className="nav-group nav-pinned">
          <NavLink
            to="/clients"
            end
            className="nav-link client-back"
            onClick={onNavigate}
            aria-label="All clients"
            title={hideLabels ? "All clients" : undefined}
          >
            <ArrowLeft aria-hidden="true" />
            {!hideLabels && <span>All clients</span>}
          </NavLink>
          {hideLabels ? (
            <span className="client-mark" title={client.name} aria-hidden="true">
              {monogram(client.name)}
            </span>
          ) : (
            <ClientSwitcher clients={list} current={client} onPick={switchTo} />
          )}
        </div>
        {CLIENT_GROUPS.map((group) => ({ group, items: group.items
          // An entry with tabs stays while any of its pages is open to this person, and links to the first.
          .map((item) => ({ item, pages: entryPages(item).filter((p) => canOpenClientPage(p, user?.role, client.service_tier, client.services_off)) }))
          .filter(({ pages }) => pages.length) }))
          .filter(({ items }) => items.length)
          .map(({ group, items }) => (
          <div className="nav-group" key={group.label || "main"}>
            {!hideLabels && group.label && <p className="nav-group-label">{group.label}</p>}
            {items.map(({ item, pages }) => (
              <NavLink
                key={item.to}
                to={clientLink(pages[0], client.id)}
                className={({ isActive }) => linkClass({ isActive: isActive || (!!item.tabs && pages.some((p) => pageMatches(pathname, query, p, item.tabs))) })}
                onClick={onNavigate}
                aria-label={item.label}
                title={hideLabels ? item.label : undefined}
              >
                <item.icon aria-hidden="true" />
                {!hideLabels && <span>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    );
  }

  /* ─────────────────────────────── LIST face ─────────────────────────────── */
  // Collapsed, the face is just the All clients icon: thirty two-letter badges
  // (fifteen of them "JK") told no one which client was which.
  const matches = list.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  return (
    <nav className="primary-nav" aria-label="Clients navigation">
      <div className="nav-group">
        <NavLink
          to="/clients"
          end
          className={linkClass}
          onClick={onNavigate}
          aria-label="All clients"
          title={hideLabels ? "All clients" : undefined}
        >
          <Building2 aria-hidden="true" />
          {!hideLabels && <span>All clients</span>}
          {!hideLabels && list.length > 0 && <span className="nav-total">{list.length}</span>}
        </NavLink>
      </div>
      {!hideLabels && (
        <div className="nav-group">
          <label className="client-nav-search"><Search aria-hidden="true"/><input aria-label="Find a client in navigation" placeholder="Find a client…" value={search} onChange={e=>setSearch(e.target.value)} /></label>
          {search && !matches.length && <p className="client-nav-note">No matching clients. <button className="text-button" onClick={()=>setSearch('')}>Clear search</button></p>}
          {list.length === 0 && <p className="client-nav-note">No clients yet.</p>}
          {matches.map((c) => {
            const inactive = c.status && c.status !== "active" ? c.status.charAt(0).toUpperCase() + c.status.slice(1) : "";
            return (
              <NavLink
                key={c.id}
                to={{ pathname: "/dashboard", search: `?client_id=${c.id}` }}
                className={() => `nav-link client-row${c.id === clientId ? " current" : ""}${inactive ? " inactive" : ""}`}
                // No setClientId here: the `?client_id=` on the link is what selects
                // the client, and setClientId would also rewrite the CURRENT URL.
                // The search is cleared so it can't linger once the client opens.
                onClick={() => { setSearch(""); onNavigate(); }}
                aria-label={inactive ? `${c.name} (${inactive})` : c.name}
                title={inactive ? `${c.name} (${inactive})` : c.name}
              >
                <span className="client-row-name">{c.name}</span>
                {inactive && <span className="client-row-status">{inactive}</span>}
              </NavLink>
            );
          })}
        </div>
      )}
    </nav>
  );
}
